import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Archive, ArchiveRestore, Pin, PinOff } from "lucide-react";
import DataTable from "bundles/Internal/components/common/DataTable";
import PipelineTableFilters from "bundles/Internal/components/pipeline/PipelineTableFilters";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

export const COLUMNS_CONSTANTS = [
  { key: "title", label: "Pipeline" },
  { key: "pipeline_type", label: "Type" },
  { key: "owner", label: "Owner" },
  { key: "assigned_users", label: "Assigned" },
  { key: "profiles_count", label: "Profiles" },
  { key: "created_at", label: "Created at" },
  { key: "status", label: "Status" },
];

const TYPE_CHIP_COLOR = {
  personal: { bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  shared: { bg: "rgba(7,147,68,0.10)", text: "#079344" },
  job_offer: { bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      borderRadius: "6px",
      height: 22,
    }}
  />
);

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const subCellSx = {
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "#2A334E",
  py: 0.75,
  borderBottom: "1px solid rgba(42,51,78,0.06)",
};

function formatPipelineCell(pipeline, key) {
  const value = pipeline?.[key];

  if (key === "title") {
    return (
      <Box>
        <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
          {value || "Untitled pipeline"}
        </Box>
        {pipeline?.company_name && (
          <Box component="span" sx={{ display: "block", fontSize: 11, color: "rgba(42, 51, 78, 0.5)" }}>
            {pipeline.company_name}
          </Box>
        )}
      </Box>
    );
  }

  if (key === "pipeline_type" && value) {
    const label = value === "job_offer" ? "Job offer" : value;
    return pill(label, TYPE_CHIP_COLOR[value] || TYPE_CHIP_COLOR.personal);
  }

  if (key === "status") {
    if (pipeline.archived) {
      return pill("Archived", { bg: "rgba(211,47,47,0.10)", text: "#D32F2F" });
    }
    return pill("Active", { bg: "rgba(7,147,68,0.10)", text: "#079344" });
  }

  if (key === "owner") {
    const owner = pipeline?.owner;
    if (!owner) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <UserAvatar user={owner} size={22} />
        <Box component="span">{owner.full_name || owner.email || "Unknown"}</Box>
      </Box>
    );
  }

  if (key === "profiles_count") {
    return value || 0;
  }

  if (key === "assigned_users") {
    const users = (value || []).filter((u) => u.role !== "admin" && u.role !== "coordinator");
    if (users.length === 0) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {users.slice(0, 4).map((u) => (
          <Tooltip key={u.id} title={u.full_name || u.email} arrow placement="top">
            <UserAvatar
              user={u}
              size={22}
              sx={{ ml: "-4px", "&:first-of-type": { ml: 0 } }}
              tooltip={false}
            />
          </Tooltip>
        ))}
        {users.length > 4 && (
          <Box component="span" sx={{ fontSize: 11, color: "rgba(42, 51, 78, 0.5)", ml: 0.5 }}>
            +{users.length - 4}
          </Box>
        )}
      </Box>
    );
  }

  if (key === "created_at") {
    return formatDateTime(value);
  }

  if (value === null || value === undefined) return "";
  return String(value);
}

const PipelineTable = ({
  pipelines = [],
  onPipelineSelect,
  onQueryChange,
  initialQuery = "",
  onTogglePin,
  onArchive,
  onUnarchive,
  onCreateClick,
  onToggleJobOfferField,
  filters,
  onFiltersChange,
  owners = [],
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
}) => {
  const columns = useMemo(() => COLUMNS_CONSTANTS, []);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPipeline, setMenuPipeline] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const getRowSx = useCallback((pipeline) => {
    const offers = pipeline.job_offers || [];
    const now = Date.now();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;

    const hasExpiring = offers.some((jo) => {
      if (!jo.end_of_mandate) return false;
      const diff = new Date(jo.end_of_mandate).getTime() - now;
      return diff >= 0 && diff <= sevenDays;
    });

    if (hasExpiring) {
      return {
        backgroundColor: "rgba(211, 47, 47, 0.06)",
        "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.10)" },
      };
    }
    return {};
  }, []);

  // Whether any visible pipeline has a job offer expiring within 7 days — this
  // drives the red rows, so the filter bar shows a legend explaining the colour.
  const hasExpiringOffer = useMemo(() => {
    const now = Date.now();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    return (pipelines || []).some((p) =>
      (p.job_offers || []).some((jo) => {
        if (!jo.end_of_mandate) return false;
        const diff = new Date(jo.end_of_mandate).getTime() - now;
        return diff >= 0 && diff <= sevenDays;
      })
    );
  }, [pipelines]);

  const openMenu = (e, pipeline) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuPipeline(pipeline);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuPipeline(null);
  };

  const renderCell = useCallback(
    (pipeline, key) => formatPipelineCell(pipeline, key),
    [],
  );

  const toggleExpand = useCallback((e, pipelineId) => {
    e.stopPropagation();
    setExpandedRows((prev) => ({ ...prev, [pipelineId]: !prev[pipelineId] }));
  }, []);

  const renderExpandedRow = useCallback(
    (pipeline) => {
      if (!expandedRows[pipeline.id]) return null;
      const offers = pipeline.job_offers || [];
      if (offers.length === 0) {
        return (
          <Box sx={{ px: 4, py: 2, fontSize: 13, color: "rgba(42,51,78,0.5)" }}>
            No job offers linked to this pipeline.
          </Box>
        );
      }
      return (
        <Collapse in timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, py: 1.5, backgroundColor: "rgba(249,250,255,0.7)" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Company", "Job Offer", "Location", "Sent from Talent Team", "End of Mandate", "Urgent", "On Hold"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#2A334E",
                        borderBottom: "1px solid rgba(42,51,78,0.1)",
                        py: 1,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {offers.map((jo) => {
                  let joRowSx = { "&:last-child td": { borderBottom: 0 } };
                  const isExpired = jo.end_of_mandate && new Date(jo.end_of_mandate).getTime() < Date.now();
                  if (jo.end_of_mandate) {
                    const diff = new Date(jo.end_of_mandate).getTime() - Date.now();
                    const sevenDays = 7 * 24 * 60 * 60 * 1000;
                    if (diff < 0) {
                      joRowSx = { ...joRowSx, opacity: 0.5, backgroundColor: "rgba(42,51,78,0.03)" };
                    } else if (diff <= sevenDays) {
                      joRowSx = { ...joRowSx, backgroundColor: "rgba(211,47,47,0.06)" };
                    }
                  }
                  return (
                    <TableRow key={jo.id} sx={joRowSx}>
                      <TableCell sx={subCellSx}>{jo.company_name || "—"}</TableCell>
                      <TableCell sx={subCellSx}>{jo.title || "—"}</TableCell>
                      <TableCell sx={subCellSx}>{jo.location || "—"}</TableCell>
                      <TableCell sx={subCellSx}>{jo.candidates_sent ?? 0}</TableCell>
                      <TableCell sx={subCellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          {jo.end_of_mandate ? formatDate(jo.end_of_mandate) : "—"}
                          {isExpired && (
                            <Chip
                              label="Expired"
                              size="small"
                              sx={{
                                backgroundColor: "rgba(211,47,47,0.10)",
                                color: "#D32F2F",
                                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                                fontSize: 10,
                                borderRadius: "4px",
                                height: 18,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...subCellSx, py: 0 }}>
                        <Checkbox
                          size="small"
                          checked={!!jo.urgent}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => onToggleJobOfferField?.(pipeline.id, jo.pipeline_job_offer_id, "urgent", e.target.checked)}
                          sx={{ p: 0.5, color: "rgba(42,51,78,0.3)", "&.Mui-checked": { color: "#D32F2F" } }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...subCellSx, py: 0 }}>
                        <Checkbox
                          size="small"
                          checked={!!jo.on_hold}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => onToggleJobOfferField?.(pipeline.id, jo.pipeline_job_offer_id, "on_hold", e.target.checked)}
                          sx={{ p: 0.5, color: "rgba(42,51,78,0.3)", "&.Mui-checked": { color: "#F57C00" } }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      );
    },
    [expandedRows, onToggleJobOfferField],
  );

  return (
    <>
      <DataTable
        title="Pipelines"
        totalLabel={(count) => `${count.toLocaleString()} total pipelines`}
        emptyIcon={PersonIcon}
        emptyTitle="No pipelines found"
        emptySubtitle="Try adjusting your search criteria"
        columns={columns}
        data={pipelines}
        renderCell={renderCell}
        extraHeadColumns={["", "Pin"]}
        renderExtraCells={(pipeline) => (
          <>
            <TableCell key="expand" sx={{ whiteSpace: "nowrap", py: 1.5, width: 36, px: 0.5 }}>
              <IconButton
                size="small"
                aria-label={expandedRows[pipeline.id] ? "Collapse" : "Expand"}
                onClick={(e) => toggleExpand(e, pipeline.id)}
                sx={{
                  color: expandedRows[pipeline.id] ? "rgba(42, 51, 78, 0.7)" : "rgba(42, 51, 78, 0.3)",
                  "&:hover": { color: "rgba(42, 51, 78, 0.7)" },
                  transition: "transform 0.2s",
                }}
              >
                {expandedRows[pipeline.id] ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
              </IconButton>
            </TableCell>
            <TableCell key="pin" sx={{ whiteSpace: "nowrap", py: 1.5, width: 48 }}>
              <Tooltip title={pipeline.pinned ? "Unpin pipeline" : "Pin pipeline"} arrow placement="top">
                <IconButton
                  size="small"
                  aria-label={pipeline.pinned ? "Unpin" : "Pin"}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTogglePin?.(pipeline);
                  }}
                  sx={{
                    color: pipeline.pinned ? "rgba(42, 51, 78, 0.7)" : "rgba(42, 51, 78, 0.25)",
                    "&:hover": { color: "rgba(42, 51, 78, 0.7)" },
                  }}
                >
                  {pipeline.pinned ? <PinOff size={16} strokeWidth={1.8} /> : <Pin size={16} strokeWidth={1.8} />}
                </IconButton>
              </Tooltip>
            </TableCell>
          </>
        )}
        renderExpandedRow={renderExpandedRow}
        getRowSx={getRowSx}
        tailHeadColumns={[""]}
        renderTailCells={(pipeline) => (
          <TableCell key="actions" sx={{ whiteSpace: "nowrap", py: 1.5, width: 48 }}>
            <IconButton
              size="small"
              aria-label="Actions"
              onClick={(e) => openMenu(e, pipeline)}
              sx={{
                color: "rgba(42, 51, 78, 0.4)",
                "&:hover": { color: "rgba(42, 51, 78, 0.7)" },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </TableCell>
        )}
        onRowClick={onPipelineSelect}
        onAddClick={onCreateClick}
        addButtonLabel="Create pipeline"
        onQueryChange={onQueryChange}
        initialQuery={initialQuery}
        searchPlaceholder="Search by name, company..."
        searchInfo="Name, company"
        filtersComponent={
          filters && onFiltersChange ? (
            <PipelineTableFilters
              filters={filters}
              onFiltersChange={onFiltersChange}
              owners={owners}
              hasExpiringOffer={hasExpiringOffer}
            />
          ) : null
        }
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        loading={loading}
      />
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 160,
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
            },
          },
        }}
      >
        {menuPipeline?.archived ? (
          <MenuItem
            onClick={() => { onUnarchive?.(menuPipeline); closeMenu(); }}
            sx={{ fontSize: 13 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ArchiveRestore size={16} strokeWidth={1.8} />
            </ListItemIcon>
            <ListItemText>Unarchive</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => { onArchive?.(menuPipeline); closeMenu(); }}
            sx={{ fontSize: 13 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Archive size={16} strokeWidth={1.8} />
            </ListItemIcon>
            <ListItemText>Archive</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default PipelineTable;
